import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';

interface DrbfmPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Appended to the dialog title, as in "…プレビュー - カバー". */
  subject?: string;
  isLoading: boolean;
  error: string | null;
  html: string;
  footer?: React.ReactNode;
}

const DrbfmPreviewDialog: React.FC<DrbfmPreviewDialogProps> = ({
  open,
  onClose,
  subject,
  isLoading,
  error,
  html,
  footer,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/*
        Three coupled rules keep the srcDoc iframe from collapsing: the fixed
        90vh height, the 1fr body row, and the iframe filling that row. The
        built-in close is hidden because the header carries a localized one.
      */}
      <DialogContent
        className="revy-ui grid h-[90vh] w-[calc(100%-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 p-0 sm:max-w-[1200px] [&>button]:hidden"
        aria-describedby={undefined}
      >
        <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0 p-6">
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {t('drbfm.history.previewTitle', 'DRBFM分析結果プレビュー')}
            {subject ? ` - ${subject}` : ''}
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.close', '閉じる')}
            onClick={onClose}
          >
            <X strokeWidth={1.9} />
          </Button>
        </DialogHeader>

        <div className="flex min-h-0 flex-col">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center gap-4 p-8">
              <Spinner label="" />
              <span className="revy-cjk text-sm leading-[1.5]">
                {t('common.loading', '読み込み中...')}
              </span>
            </div>
          ) : error ? (
            <div className="p-6">
              <Alert variant="destructive">
                <AlertDescription className="revy-cjk leading-[1.6]">{error}</AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-hidden">
              <iframe
                srcDoc={html}
                title={t('drbfm.history.previewFrameTitle', 'DRBFM Preview')}
                sandbox="allow-same-origin"
                className="h-full w-full border-0"
              />
            </div>
          )}
        </div>

        {footer ? <div className="flex justify-end gap-2 p-6">{footer}</div> : <div />}
      </DialogContent>
    </Dialog>
  );
};

export default DrbfmPreviewDialog;
